import {
  IChildRowCustomFieldConfig,
  ILabel,
  ILabelOption,
} from '../constants/interfaces';
import { formUi } from '../constants/globalcss';
import { IFormStateValue } from '../utils/formUtils';
import { isFieldVisible } from '../utils/visibilityUtils';

// ─── Types ───────────────────────────────────────────────────────────────────

export type Row = {
  customFields: IChildRowCustomFieldConfig[];
  customValues: Record<string, string>;
  id: string;
  /** Required when line item is "Other"; sent as `description` in the API JSON. */
  otherDescription: string;
  selectedText: string;
  response: string;
  neighborComplaint: string;
  codeIssue: string;
  preservationOrCosmetic: string;
  isEmergency: string;
  willPicturesAttached: string;
};

export type RowErrors = {
  neighborComplaint?: string;
  codeIssue?: string;
  preservationOrCosmetic?: string;
  amount?: string;
  customByKey?: Record<string, string>;
  otherDescription?: string;
  isEmergency?: string;
  willPicturesAttached?: string;
};

// ─── Option constants ─────────────────────────────────────────────────────────

export const URGENCY_OPTIONS = [
  {
    value: 'Preservation',
    label: 'Preservation',
    tooltip: '24 hour timespan, safety or regulatory hazard.',
  },
  {
    value: 'Cosmetic',
    label: 'Cosmetic',
    tooltip: '3 days timespan, prevent imminent loss of value or legal risk.',
  },
] as const;

export const YES_NO_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
] as const;

// ─── Style constants ──────────────────────────────────────────────────────────

export const WORKFLOW_TABLE_ROW_STRIPE = 'rgba(245, 245, 245, 0.7)';
export const READABLE_PLACEHOLDER = 'rgba(51, 51, 64, 0.72)';
export const totalFieldWidth = '220px';

export const tableFieldOutlineSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: formUi.card.radius,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(22, 56, 96, 0.28)',
  },
  '& .MuiOutlinedInput-root:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline':
    {
      borderColor: 'rgba(22, 56, 96, 0.42)',
    },
} as const;

export const childRowGridItemSx = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  width: '100%',
} as const;

// ─── Row helpers ──────────────────────────────────────────────────────────────

export function isOtherLineItem(selectedText: string): boolean {
  return selectedText.trim().toLowerCase() === 'other';
}

export function makeRowId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export function emptyRow(): Row {
  return {
    customFields: [],
    customValues: {},
    id: makeRowId(),
    otherDescription: '',
    selectedText: '',
    response: '',
    neighborComplaint: '',
    codeIssue: '',
    preservationOrCosmetic: '',
    isEmergency: '',
    willPicturesAttached: '',
  };
}

export function valueKeyForCustomField(
  cf: IChildRowCustomFieldConfig,
  index: number
): string {
  if (cf.labelId != null) return String(cf.labelId);
  return `__cf_${index}`;
}

export function getLineItemNestedLabels(
  summationField: ILabel,
  row: Row
): ILabel[] | undefined {
  const childDef = summationField.children?.find(
    (c) => c.text === row.selectedText
  );
  return childDef?.children;
}

export function buildMergedFormStateForChildRow(
  globalFormState: Record<string, IFormStateValue> | undefined,
  row: Row
): Record<string, IFormStateValue> {
  const merged: Record<string, IFormStateValue> = globalFormState
    ? { ...globalFormState }
    : {};
  row.customFields.forEach((cf, i) => {
    const vk = valueKeyForCustomField(cf, i);
    merged[vk] = { value: row.customValues[vk] ?? '', children: [] };
  });
  return merged;
}

export function isCustomFieldVisibleAtIndex(
  cf: IChildRowCustomFieldConfig,
  index: number,
  nestedLabels: ILabel[] | undefined,
  mergedFormState: Record<string, IFormStateValue>
): boolean {
  if (cf.labelId == null) return true;
  const labelNode = nestedLabels?.find((l) => l.labelId === cf.labelId);
  if (!labelNode) return true;
  return isFieldVisible(labelNode, mergedFormState);
}

// ─── API payload ──────────────────────────────────────────────────────────────

export function buildRowApiResponse(
  row: Row,
  nestedLabels: ILabel[] | undefined,
  globalFormState: Record<string, IFormStateValue> | undefined
): string {
  const merged = buildMergedFormStateForChildRow(globalFormState, row);
  const description = isOtherLineItem(row.selectedText)
    ? 'Other'
    : row.selectedText;
  const payload: Record<string, string> = {
    amount: row.response,
    neighborComplaint: row.neighborComplaint,
    codeIssue: row.codeIssue,
    preservationOrCosmetic: row.preservationOrCosmetic,
    isEmergency: row.isEmergency,
    willPicturesAttached: row.willPicturesAttached,
    description,
  };
  row.customFields.forEach((cf, i) => {
    const apiKey = cf.key?.trim();
    if (!apiKey) return;
    if (
      cf.labelId != null &&
      nestedLabels &&
      !isCustomFieldVisibleAtIndex(cf, i, nestedLabels, merged)
    ) {
      return;
    }
    payload[apiKey] = row.customValues[valueKeyForCustomField(cf, i)] ?? '';
  });
  return JSON.stringify(payload);
}

export function rowsToChildren(
  rows: Row[],
  childrenDefs: ILabel[] | undefined,
  globalFormState?: Record<string, IFormStateValue>
): { labelId: number; response: string }[][] {
  return rows
    .filter((r) => r.selectedText)
    .map((r) => {
      const child = childrenDefs?.find((c) => c.text === r.selectedText);
      return [
        {
          labelId: child?.labelId ?? 0,
          response: buildRowApiResponse(r, child?.children, globalFormState),
        },
      ];
    });
}

export function sumRowValues(rows: Row[]): string {
  const sum = rows
    .filter((r) => r.selectedText)
    .reduce((acc, r) => acc + (parseFloat(r.response) || 0), 0);
  return sum.toFixed(2);
}

// ─── Validation ───────────────────────────────────────────────────────────────

export function rowFieldErrors(
  r: Row,
  amountRequired: boolean,
  nestedLabels: ILabel[] | undefined,
  globalFormState: Record<string, IFormStateValue> | undefined
): RowErrors {
  if (!r.selectedText) return {};
  const merged = buildMergedFormStateForChildRow(globalFormState, r);
  const out: RowErrors = {};

  if (!r.neighborComplaint.trim()) out.neighborComplaint = 'Required';
  if (!r.codeIssue.trim()) out.codeIssue = 'Required';
  if (!r.preservationOrCosmetic.trim()) out.preservationOrCosmetic = 'Required';
  if (!r.isEmergency.trim()) out.isEmergency = 'Required';
  if (!r.willPicturesAttached.trim()) out.willPicturesAttached = 'Required';
  if (amountRequired && !r.response.trim()) out.amount = 'Required';

  const customByKey: Record<string, string> = {};
  r.customFields.forEach((cf, i) => {
    if (!cf.required) return;
    if (!isCustomFieldVisibleAtIndex(cf, i, nestedLabels, merged)) return;
    const vk = valueKeyForCustomField(cf, i);
    if (!(r.customValues[vk] ?? '').trim()) customByKey[vk] = 'Required';
  });
  if (Object.keys(customByKey).length > 0) out.customByKey = customByKey;

  if (isOtherLineItem(r.selectedText) && !r.otherDescription.trim()) {
    out.otherDescription = 'Required';
  }
  return out;
}

// ─── Display ──────────────────────────────────────────────────────────────────

export function formatRowForDisplay(
  r: Row,
  field: ILabel,
  globalFormState: Record<string, IFormStateValue> | undefined
): string {
  const lineLabel = isOtherLineItem(r.selectedText)
    ? r.otherDescription.trim()
      ? `Other (${r.otherDescription.trim()})`
      : 'Other'
    : r.selectedText;
  const parts: string[] = [lineLabel];
  if (r.response) parts.push(`Amount: ${r.response}`);
  if (r.neighborComplaint)
    parts.push(`Neighbor Complaint: ${r.neighborComplaint}`);
  if (r.codeIssue) parts.push(`Code Issue: ${r.codeIssue}`);
  if (r.preservationOrCosmetic)
    parts.push(`Preservation/Cosmetic: ${r.preservationOrCosmetic}`);
  if (r.isEmergency) parts.push(`Emergency: ${r.isEmergency}`);
  if (r.willPicturesAttached)
    parts.push(`Pictures Attached: ${r.willPicturesAttached}`);
  const nested = getLineItemNestedLabels(field, r);
  const merged = buildMergedFormStateForChildRow(globalFormState, r);
  r.customFields.forEach((cf, i) => {
    if (!isCustomFieldVisibleAtIndex(cf, i, nested, merged)) return;
    const v = (r.customValues[valueKeyForCustomField(cf, i)] ?? '').trim();
    if (cf.label && v) parts.push(`${cf.label}: ${v}`);
  });
  return parts.join(' · ');
}

// ─── Custom field config parsing ──────────────────────────────────────────────

function normalizeCustomFieldConfig(
  config: IChildRowCustomFieldConfig | string | null | undefined
): IChildRowCustomFieldConfig | null {
  const normalizeObject = (
    candidate: IChildRowCustomFieldConfig | Record<string, unknown>
  ): IChildRowCustomFieldConfig | null => {
    const hasKnownKeys =
      Object.prototype.hasOwnProperty.call(candidate, 'type') ||
      Object.prototype.hasOwnProperty.call(candidate, 'label') ||
      Object.prototype.hasOwnProperty.call(candidate, 'options') ||
      Object.prototype.hasOwnProperty.call(candidate, 'key') ||
      Object.prototype.hasOwnProperty.call(candidate, 'required');
    if (hasKnownKeys) return candidate as IChildRowCustomFieldConfig;
    const nested = (candidate as { customField?: unknown }).customField;
    if (nested && typeof nested === 'object') {
      return normalizeObject(nested as Record<string, unknown>);
    }
    return null;
  };

  if (!config) return null;
  if (typeof config === 'string') {
    try {
      const parsed = JSON.parse(config) as
        | IChildRowCustomFieldConfig
        | Record<string, unknown>;
      return parsed && typeof parsed === 'object'
        ? normalizeObject(parsed)
        : null;
    } catch {
      return null;
    }
  }
  return normalizeObject(config as Record<string, unknown>);
}

function parseCustomFieldsFromSource(
  source: unknown
): IChildRowCustomFieldConfig[] {
  if (source == null) return [];
  if (typeof source === 'string') {
    try {
      return parseCustomFieldsFromSource(JSON.parse(source) as unknown);
    } catch {
      return [];
    }
  }
  if (Array.isArray(source)) {
    return source
      .map((item) =>
        normalizeCustomFieldConfig(item as IChildRowCustomFieldConfig | string)
      )
      .filter((x): x is IChildRowCustomFieldConfig => x != null);
  }
  const one = normalizeCustomFieldConfig(source as IChildRowCustomFieldConfig);
  return one ? [one] : [];
}

function getResponseKeyFromLabelRules(nested: ILabel): string {
  if (!nested.rules?.trim()) return String(nested.labelId);
  try {
    const parsed = JSON.parse(nested.rules) as { responseKey?: string };
    const k = parsed.responseKey?.trim();
    if (k) return k;
  } catch {
    /* ignore */
  }
  return String(nested.labelId);
}

export function getCustomFieldConfigsFromChild(
  child: ILabel | undefined
): IChildRowCustomFieldConfig[] {
  if (!child) return [];
  if (child.children && child.children.length > 0) {
    const sorted = [...child.children].sort(
      (a, b) => Number(a.orderIndex) - Number(b.orderIndex)
    );
    return sorted.map((nested) => ({
      key: getResponseKeyFromLabelRules(nested),
      labelId: nested.labelId,
      label: nested.text,
      options: nested.options?.map((o) => ({ label: o.text, value: o.text })),
      required: nested.isMandatory,
      type: nested.type,
    }));
  }
  if (child.rules) {
    try {
      const parsed = JSON.parse(child.rules) as { customField?: unknown };
      return parseCustomFieldsFromSource(parsed.customField);
    } catch {
      return [];
    }
  }
  return [];
}

// ─── Field builder ────────────────────────────────────────────────────────────

export function buildSelectFieldForRow(
  field: ILabel,
  row: Row,
  allRows: Row[]
): ILabel {
  const takenTexts = new Set(
    allRows
      .filter((r) => r.id !== row.id && r.selectedText)
      .map((r) => r.selectedText)
  );
  const options: ILabelOption[] = (field.children || [])
    .filter((c) => !takenTexts.has(c.text) || c.text === row.selectedText)
    .map((c) => ({
      customField: getCustomFieldConfigsFromChild(c),
      isDefault: false,
      labelOptionId: c.labelId,
      parentLabelId: field.labelId,
      orderIndex: c.orderIndex,
      text: c.text,
    }));
  return {
    formula: null,
    isMandatory: field.isMandatory,
    labelId: field.labelId,
    options,
    orderIndex: field.orderIndex,
    rules: null,
    text: field.text,
    type: 'dropdown',
  };
}
